import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Search, TicketPlus, ChevronLeft, ChevronRight, RefreshCw, FilterIcon } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { format, startOfDay, endOfDay, subHours, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subDays } from 'date-fns';

const ClientTickets = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [productFilter, setProductFilter] = useState('all');
  const [createdRange, setCreatedRange] = useState('all');
  const [createdFrom, setCreatedFrom] = useState('');
  const [createdTo, setCreatedTo] = useState('');
  const [products, setProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalTickets, setTotalTickets] = useState(0);
  const itemsPerPage = 20;

  useEffect(() => {
    // Fetch user's assigned products
    fetchUserProducts();
  }, []);

  useEffect(() => {
    fetchTickets();
  }, [currentPage, search, statusFilter, productFilter, createdFrom, createdTo, createdRange]);

  const fetchUserProducts = async () => {
    try {
      const response = await api.get('/products');
      // Filter to only show products assigned to this client
      const userProductIds = user?.product_ids || [];
      const userProducts = response.data.filter(p => userProductIds.includes(p.id));
      setProducts(userProducts);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const params = {
        skip: (currentPage - 1) * itemsPerPage,
        limit: itemsPerPage,
      };
      if (search) params.search = search;
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (productFilter && productFilter !== 'all') params.product_id = productFilter;

      // Handle created date presets or custom range
      if (createdRange && createdRange !== 'custom' && createdRange !== 'all') {
        let fromDate, toDate;
        const now = new Date();
        switch (createdRange) {
          case 'today':
            fromDate = startOfDay(now);
            toDate = endOfDay(now);
            break;
          case 'yesterday':
            fromDate = startOfDay(subDays(now, 1));
            toDate = endOfDay(subDays(now, 1));
            break;
          case 'last_24hr':
            fromDate = subHours(now, 24);
            toDate = now;
            break;
          case 'this_week':
            fromDate = startOfWeek(now, { weekStartsOn: 1 });
            toDate = endOfWeek(now, { weekStartsOn: 1 });
            break;
          case 'this_month':
            fromDate = startOfMonth(now);
            toDate = endOfMonth(now);
            break;
          default:
            fromDate = null;
            toDate = null;
        }
        if (fromDate) params.created_from = format(fromDate, 'yyyy-MM-dd');
        if (toDate) params.created_to = format(toDate, 'yyyy-MM-dd');
      } else {
        if (createdFrom) params.created_from = createdFrom;
        if (createdTo) params.created_to = createdTo;
      }

      const response = await api.get('/tickets', { params });
      setTickets(response.data.items || []);
      setTotalTickets(response.data.total || 0);
    } catch (error) {
      toast.error('Failed to fetch tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchTickets();
  };

  const handleResetFilters = () => {
    setSearch('');
    setStatusFilter('all');
    setProductFilter('all');
    setCreatedRange('all');
    setCreatedFrom('');
    setCreatedTo('');
    setCurrentPage(1);
  };

  const todayStr = format(new Date(), 'yyyy-MM-dd');

  const handleCreatedFromChange = (value) => {
    if (!value) {
      setCreatedFrom('');
      return;
    }
    // Clamp to today if a future date selected
    const v = value > todayStr ? todayStr : value;
    setCreatedFrom(v);
    // If to exists and is before from, move to to = from
    if (createdTo && createdTo !== '' && v > createdTo) {
      setCreatedTo(v);
    }
  };

  const handleCreatedToChange = (value) => {
    if (!value) {
      setCreatedTo('');
      return;
    }
    const v = value > todayStr ? todayStr : value;
    setCreatedTo(v);
    // If from exists and is after to, move from = to
    if (createdFrom && createdFrom !== '' && v < createdFrom) {
      setCreatedFrom(v);
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalTickets / itemsPerPage));

  const statusColors = {
    'Open': 'bg-amber-50 text-amber-700 border-amber-200',
    'In Progress': 'bg-blue-50 text-blue-700 border-blue-200',
    'Resolved': 'bg-emerald-50 text-emerald-700 border-emerald-200',
    'Closed': 'bg-slate-100 text-slate-600 border-slate-200',
  };

  return (
    <div className="space-y-4 md:space-y-6" data-testid="client-tickets-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">My Tickets</h1>
          <p className="text-xs sm:text-sm text-slate-600 mt-1">View and manage your support tickets</p>
        </div>
        <Button
          onClick={() => navigate('/tickets/new')}
          data-testid="create-ticket-btn"
          className="bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-700 hover:to-indigo-600 text-white font-medium shadow-md shadow-indigo-500/25 text-xs sm:text-sm w-full sm:w-auto"
        >
          <TicketPlus className="mr-2 h-3.5 w-3.5 sm:h-4 sm:w-4" />
          Create Ticket
        </Button>
      </div>

      {/* Filters */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-3 sm:p-4 md:p-6">
          <div className="flex flex-col gap-2.5 sm:gap-3 md:gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
              <Input
                placeholder="Search tickets..."
                data-testid="search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-9 border-slate-300 focus:border-indigo-500 focus:ring-indigo-500 text-xs sm:text-sm"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3 md:gap-4">
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setCurrentPage(1); }}>
                <SelectTrigger data-testid="status-filter" className="border-slate-300 focus:border-indigo-500 focus:ring-indigo-500 text-xs sm:text-sm">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="Open">Open</SelectItem>
                  <SelectItem value="In Progress">In Progress</SelectItem>
                  <SelectItem value="Resolved">Resolved</SelectItem>
                  <SelectItem value="Closed">Closed</SelectItem>
                </SelectContent>
              </Select>

              <Select value={productFilter} onValueChange={(v) => { setProductFilter(v); setCurrentPage(1); }}>
                <SelectTrigger data-testid="product-filter" className="border-slate-300 focus:border-indigo-500 focus:ring-indigo-500 text-xs sm:text-sm">
                  <SelectValue placeholder="Product" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Products</SelectItem>
                  {products.map((p) => (
                    <SelectItem key={p.id} value={p.id.toString()}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={createdRange} onValueChange={(v) => { setCreatedRange(v); setCurrentPage(1); }}>
                <SelectTrigger data-testid="created-range" className="border-slate-300 focus:border-indigo-500 focus:ring-indigo-500 text-xs sm:text-sm">
                  <SelectValue placeholder="Created" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any Time</SelectItem>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="yesterday">Yesterday</SelectItem>
                  <SelectItem value="last_24hr">Last 24hr</SelectItem>
                  <SelectItem value="this_week">This week</SelectItem>
                  <SelectItem value="this_month">This month</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>  
                </SelectContent>
              </Select>
            </div>

            {createdRange === 'custom' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3 md:gap-4">
                <div className="space-y-1">
                  <label className="text-xs sm:text-sm text-slate-600">From</label>
                  <div className="relative">
                    <Input
                      type="date"
                      value={createdFrom || ''}
                      onChange={(e) => handleCreatedFromChange(e.target.value)}
                      className="border-slate-300 w-full pl-14"
                      max={todayStr}
                      aria-label="Created from"
                      data-testid="created-from"
                    />
                  </div>
                </div>
                <div className="w-full lg:w-auto space-y-2">
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">To</span>
                    <Input
                      type="date"
                      value={createdTo || ''}
                      onChange={(e) => handleCreatedToChange(e.target.value)}
                      className="border-slate-300 w-full pl-14"
                      max={todayStr}
                      aria-label="Created to"
                      data-testid="created-to"
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="flex gap-2">
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button onClick={handleSearch} data-testid="apply-filters-btn" className="bg-indigo-600 hover:bg-indigo-700 text-white" aria-label="Apply filters">
                      <FilterIcon className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Apply filters</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button onClick={handleResetFilters} data-testid="reset-filters-btn" variant="outline" className="border-slate-300 text-slate-700" aria-label="Reset filters">
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Reset filters</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tickets List */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-6">
          {loading ? (
            <div className="space-y-3">
              {Array(5).fill(0).map((_, i) => (
                <div key={i} className="p-5 border border-slate-200 rounded-xl">
                  <Skeleton className="h-6 w-full mb-2" />
                  <Skeleton className="h-4 w-3/4" />
                </div>
              ))}
            </div>
          ) : tickets.length === 0 ? (
            <div className="p-12 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-100 mb-4">
                <TicketPlus className="w-8 h-8 text-slate-400" />
              </div>
              <p className="text-slate-600 font-medium">No tickets found</p>
              <p className="text-slate-500 text-sm mt-1">Create your first ticket to get started</p>
            </div>
          ) : (
            <div className="space-y-3">
              {tickets.map((ticket) => {
                const product = products.find(p => p.id === ticket.product_id);
                return (
                  <div
                    key={ticket.id}
                    data-testid={`ticket-${ticket.id}`}
                    className="group p-5 border border-slate-200 dark:border-slate-700 dark:bg-slate-900/80 rounded-xl hover:border-indigo-300 dark:hover:border-indigo-400 hover:shadow-md transition-all cursor-pointer bg-white"
                    onClick={() => navigate(`/tickets/${ticket.id}`)}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span className="text-sm font-mono font-semibold text-indigo-600">
                            {ticket.ticket_id}
                          </span>
                          <Badge className={`${statusColors[ticket.status]} border px-2.5 py-0.5`}>
                            {ticket.status}
                          </Badge>
                          {product && (
                            <Badge variant="outline" className="border px-2 py-0.5 text-xs font-medium">
                              {product.name}
                            </Badge>
                          )}
                        </div>
                        <h3 className="text-lg font-semibold text-slate-900 mb-1 group-hover:text-indigo-600 transition-colors">
                          {ticket.title}
                        </h3>
                        <p className="text-sm text-slate-500">
                          Created <strong>{ticket.created_at ? format(new Date(ticket.created_at), 'MMM dd, yyyy HH:mm') : '—'}</strong>
                          {ticket.updated_at && (
                            <> • Updated: <strong>{format(new Date(ticket.updated_at), 'MMM dd, yyyy HH:mm')}</strong></>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {!loading && totalTickets > 0 && (
            <div className="flex items-center justify-between mt-6 pt-6 border-t border-slate-200">
              <p className="text-sm text-slate-600">
                Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalTickets)} of {totalTickets} tickets
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="border-slate-300"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <Button
                      key={pageNum}
                      variant={currentPage === pageNum ? "default" : "outline"}
                      size="sm"
                      onClick={() => setCurrentPage(pageNum)}
                      className={currentPage === pageNum ? "bg-indigo-600 hover:bg-indigo-700 text-white" : "border-slate-300"}
                    >
                      {pageNum}
                    </Button>
                  );
                })}
                {totalPages > 5 && currentPage < totalPages - 2 && (
                  <>
                    <span className="px-2 py-1 text-slate-500">...</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(totalPages)}
                      className="border-slate-300"
                    >
                      {totalPages}
                    </Button>
                  </>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="border-slate-300"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ClientTickets;
